import { IRecentChat } from 'api/concierge';

export interface RecentChatDateGroup {
    key: string;
    /** Translation key for the relative groups; month and year groups carry a ready label instead. */
    labelKey?: string;
    label?: string;
    chats: IRecentChat[];
}

const DAY_MS = 86_400_000;

// Stored stamps are naive DATETIMEs in the app zone (UTC by default); without a zone Date would read them as local.
function activityDate(value: string): Date | null {
    const iso = value.trim().replace(' ', 'T');
    const date = new Date(/(Z|[+-]\d{2}:?\d{2})$/i.test(iso) ? iso : `${iso}Z`);
    return Number.isNaN(date.getTime()) ? null : date;
}

function startOfDay(date: Date): number {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

// Finder-style dividers on the viewer's calendar: Today, Previous 7 Days, Previous 30 Days, then one per month of the
// current year and one per earlier year. Rows keep their order (newest activity first), so groups come out in order too.
export function groupByDate(chats: IRecentChat[], now: Date = new Date(), locale = 'en'): RecentChatDateGroup[] {
    const today = startOfDay(now);
    const groups = new Map<string, RecentChatDateGroup>();
    const add = (group: Omit<RecentChatDateGroup, 'chats'>, chat: IRecentChat) => {
        const existing = groups.get(group.key);
        if (existing) existing.chats.push(chat);
        else groups.set(group.key, { ...group, chats: [chat] });
    };

    chats.forEach((chat) => {
        const date = activityDate(chat.last_activity_time);
        const daysAgo = date ? Math.round((today - startOfDay(date)) / DAY_MS) : 0;
        if (!date || daysAgo <= 0) add({ key: 'today', labelKey: 'recent_chats.today' }, chat);
        else if (daysAgo <= 7) add({ key: 'previous_7_days', labelKey: 'recent_chats.previous_7_days' }, chat);
        else if (daysAgo <= 30) add({ key: 'previous_30_days', labelKey: 'recent_chats.previous_30_days' }, chat);
        else if (date.getFullYear() === now.getFullYear())
            add(
                {
                    key: `month-${date.getMonth()}`,
                    label: date.toLocaleString(locale, { month: 'long' }),
                },
                chat,
            );
        else add({ key: `year-${date.getFullYear()}`, label: String(date.getFullYear()) }, chat);
    });

    return Array.from(groups.values());
}
